import React, { createContext, useContext, useState, useEffect } from "react";
import { 
  allUsers, 
  mockCreator, 
  platformAdOpportunities, 
  platformAffiliateDeals, 
  platformVettedCauses 
} from "../data/mockData";

const DataContext = createContext();

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  // --- STATE INITIALIZATION ---
  
  // Users (Fans/Creators)
  const [users, setUsers] = useState(() => {
    const saved = localStorage.getItem("adnaki_users");
    return saved ? JSON.parse(saved) : allUsers;
  });

  // System Users (Admins/Staff)
  const [systemUsers, setSystemUsers] = useState(() => {
    const saved = localStorage.getItem("adnaki_system_users");
    return saved ? JSON.parse(saved) : [
       { id: 1, name: "Abebe Bikila", email: "abebe@adnaki.com", role: "Super Admin", status: "Active", lastLogin: "2h ago" },
       { id: 2, name: "Sara Tadesse", email: "sara@adnaki.com", role: "Content Moderator", status: "Active", lastLogin: "1d ago" },
       { id: 3, name: "Dawit Mekonnen", email: "dawit@adnaki.com", role: "Finance Manager", status: "Inactive", lastLogin: "5d ago" },
    ];
  });

  // Creators
  const [creators, setCreators] = useState(() => {
    const saved = localStorage.getItem("adnaki_creators");
    return saved ? JSON.parse(saved) : [mockCreator]; 
  });

  // Ad Campaigns
  const [adCampaigns, setAdCampaigns] = useState(() => {
    // ✨ Changed key to force refresh of data
    const saved = localStorage.getItem("adnaki_ad_campaigns_v2");
    return saved ? JSON.parse(saved) : platformAdOpportunities;
  });

  // Affiliates
  const [affiliates, setAffiliates] = useState(() => {
    const saved = localStorage.getItem("adnaki_affiliates");
    return saved ? JSON.parse(saved) : platformAffiliateDeals;
  });

  // Donations
  const [donations, setDonations] = useState(() => {
    const saved = localStorage.getItem("adnaki_donations");
    return saved ? JSON.parse(saved) : platformVettedCauses;
  });

  // Content (Posts/Videos)
  const [contents, setContents] = useState(() => {
    const saved = localStorage.getItem("adnaki_contents");
    return saved ? JSON.parse(saved) : [
      ...(mockCreator.posts || []).map(p => ({...p, type: 'post', creatorId: mockCreator.id}))
    ];
  });

  // Roles & Permissions
  const [systemRoles, setSystemRoles] = useState(() => {
    const saved = localStorage.getItem("adnaki_roles");
    return saved ? JSON.parse(saved) : [
      {
        id: "role_1",
        name: "Super Admin",
        description: "Full system access and control",
        permissions: ["users.manage", "creators.manage", "finance.view", "finance.manage", "settings.manage", "roles.manage", "support.manage", "monetization.manage"],
        isSystem: true 
      },
      {
        id: "role_2",
        name: "Content Moderator",
        description: "Manage content and user reports",
        permissions: ["users.view", "creators.view", "support.manage", "moderation.manage"],
        isSystem: true
      },
      {
        id: "role_3",
        name: "Finance Manager",
        description: "Manage financial operations",
        permissions: ["finance.view", "finance.manage", "monetization.view"],
        isSystem: false
      }
    ];
  });

  // Audit Logs
  const [auditLogs, setAuditLogs] = useState(() => {
    const saved = localStorage.getItem("adnaki_audit_logs");
    return saved ? JSON.parse(saved) : [
      {
        id: "log_init",
        timestamp: new Date(Date.now() - 86400000).toISOString(),
        actorId: "sys_1",
        actorName: "System",
        action: "SYSTEM_INIT",
        targetId: "platform",
        targetName: "Adnaki Platform",
        details: "System initialized with default configuration"
      }
    ];
  });

  // Payouts
  const [payouts, setPayouts] = useState(() => {
    const saved = localStorage.getItem("adnaki_payouts");
    return saved ? JSON.parse(saved) : [
      {
        id: "payout_101",
        creatorId: "c1",
        creatorName: "Gigi Kiya",
        amount: 15000,
        method: "Commercial Bank of Ethiopia",
        accountNumber: "1000114533298",
        status: "Pending",
        requestDate: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
      },
      {
        id: "payout_102",
        creatorId: "c2", 
        creatorName: "Abel Mulugeta",
        amount: 8500,
        method: "Telebirr",
        accountNumber: "0911234567",
        status: "Pending",
        requestDate: new Date(Date.now() - 43200000).toISOString(), // 12 hours ago
      }
    ];
  });

  // --- PERSISTENCE ---
  useEffect(() => { localStorage.setItem("adnaki_users", JSON.stringify(users)); }, [users]);
  useEffect(() => { localStorage.setItem("adnaki_system_users", JSON.stringify(systemUsers)); }, [systemUsers]);
  useEffect(() => { localStorage.setItem("adnaki_creators", JSON.stringify(creators)); }, [creators]);
  useEffect(() => { localStorage.setItem("adnaki_ad_campaigns_v2", JSON.stringify(adCampaigns)); }, [adCampaigns]);
  useEffect(() => { localStorage.setItem("adnaki_affiliates", JSON.stringify(affiliates)); }, [affiliates]);
  useEffect(() => { localStorage.setItem("adnaki_donations", JSON.stringify(donations)); }, [donations]);
  useEffect(() => { localStorage.setItem("adnaki_contents", JSON.stringify(contents)); }, [contents]);
  useEffect(() => { localStorage.setItem("adnaki_roles", JSON.stringify(systemRoles)); }, [systemRoles]);
  useEffect(() => { localStorage.setItem("adnaki_audit_logs", JSON.stringify(auditLogs)); }, [auditLogs]);
  useEffect(() => { localStorage.setItem("adnaki_payouts", JSON.stringify(payouts)); }, [payouts]);

  // --- ACTIONS ---
  const logAction = ({ action, targetId, targetName, actorId, actorName, details }) => {
    const newLog = {
      id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      action,
      targetId: targetId || "N/A",
      targetName: targetName || "N/A",
      actorId: actorId || "unknown",
      actorName: actorName || "Unknown User",
      details: details || ""
    };
    setAuditLogs(prev => [newLog, ...prev]);
  };

  const processPayout = (payoutId, status, note = "") => {
    setPayouts(prev => prev.map(p => 
      p.id === payoutId 
        ? { ...p, status, processedDate: new Date().toISOString(), note } 
        : p
    ));

    // Find the payout for logging
    const payout = payouts.find(p => p.id === payoutId);
    if (payout) {
        logAction({
            action: status === "Processed" ? "PAYOUT_APPROVE" : "PAYOUT_REJECT",
            targetId: payout.creatorId,
            targetName: payout.creatorName,
            actorName: "Super Admin",
            details: `${status} payout of ${payout.amount} ETB. ${note}`
        });
    }
  };

  const addAdCampaign = (campaign) => {
    setAdCampaigns(prev => {
      const exists = prev.find(c => c.id === campaign.id);
      if (exists) {
        return prev.map(c => c.id === campaign.id ? campaign : c);
      }
      return [campaign, ...prev];
    });
  };
  const addAffiliateDeal = (deal) => setAffiliates(prev => [deal, ...prev]);
  const addDonationCause = (cause) => setDonations(prev => [cause, ...prev]);
  const updateCreatorContent = (newContent) => setContents(prev => [newContent, ...prev]);
  const registerUser = (newUser) => setUsers(prev => [...prev, newUser]);
  const updateCreatorProfile = (creatorId, updates) => setCreators(prev => prev.map(c => c.id === creatorId ? { ...c, ...updates } : c));
  
  // Roles Actions
  const addRole = (role) => setSystemRoles(prev => [...prev, { ...role, id: `role_${Date.now()}` }]);
  const updateRole = (roleId, updates) => setSystemRoles(prev => prev.map(r => r.id === roleId ? { ...r, ...updates } : r));
  const deleteRole = (roleId) => setSystemRoles(prev => prev.filter(r => r.id !== roleId));

  // System User Actions
  const addSystemUser = (user) => setSystemUsers(prev => [...prev, { ...user, id: Date.now(), status: 'Active', lastLogin: 'Never' }]);
  const updateSystemUser = (id, updates) => setSystemUsers(prev => prev.map(u => u.id === id ? { ...u, ...updates } : u));
  const deleteSystemUser = (id) => setSystemUsers(prev => prev.filter(u => u.id !== id));

  // --- GETTERS ---
  const getCreatorById = (id) => creators.find(c => c.id === id);
  const getCurrentCreator = () => creators[0]; 

  const value = {
    users,
    systemUsers,
    creators,
    adCampaigns,
    affiliates,
    donations,
    contents,
    systemRoles,
    auditLogs,
    payouts,
    addAdCampaign,
    addAffiliateDeal,
    addDonationCause,
    updateCreatorContent,
    registerUser,
    updateCreatorProfile,
    getCreatorById,
    getCurrentCreator,
    addRole,
    updateRole,
    deleteRole,
    addSystemUser,
    updateSystemUser,
    deleteSystemUser,
    logAction,
    processPayout
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
